import { usePersonalQuery } from "@/app/branchs/branchs.api";
import {
  useInventoriesQuery,
  useInventoryItemsQuery,
} from "@/app/inventory/inventory.api";
import { FC, useEffect, useMemo, useState } from "react";
import { toasts } from "@/shared/utils/toasts";
import {
  useCreateMaintenanceMutation,
  useGetMaintenanceByIdMutation,
  useMaintenanceQuery,
  useUpdateMaintenanceMutation,
  useChangeMaintenanceStatusMutation,
} from "@/app/maintenance/maintenance.api";
import PaginationUI from "@/shared/components/atoms/PaginationUI";
import { DialogCreate } from "@/shared/components/molecules/DialogCreate";
import { FiltersMaintenance } from "@/shared/components/organisms/FiltersMaintenance";
import FormMaintenance from "@/shared/components/organisms/FormMaintenance";
import TableListMaintenance from "@/shared/components/organisms/TableListMaintenance";
import { useDebounce } from "@/shared/hooks/debounce";
import {
  editMaintenanceValidationSchema,
  maintenanceInitialValues,
  maintenanceValidationSchema,
} from "@/shared/schemas/maintenances.schema";
import { Formik, useFormik } from "formik";
import { t } from "i18next";
import { useNavigate, useParams } from "react-router-dom";
import AlertErrorButtons from "@/shared/components/molecules/AlertErrorButtons";
import { Permission, usePermission } from "@/shared/hooks/usePermission";

export const Maintenance: FC<any> = () => {
  const { id } = useParams();
  const { can } = usePermission();
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [currentMaintenance, setCurrentMaintenance] = useState<any>();
  const [openCreateInventoryDialog, setOpenCreateInventoryDialog] =
    useState<boolean>(false);
  const [getMaintenanceById] = useGetMaintenanceByIdMutation();
  const [updateMaintenance, updateMaintenanceResult] =
    useUpdateMaintenanceMutation();
  const [createMaintenance, createMaintenanceResult] =
    useCreateMaintenanceMutation();
  const [changeMaintenanceStatus] = useChangeMaintenanceStatusMutation();

  const personalQuery = usePersonalQuery({
    branchId: id,
    page: 1,
    limit: 10000,
  });

  const maintenanceFormik = useFormik({
    initialValues: {
      search: "",
      state: 0,
      urgency: undefined,
      assignedId: undefined,
      startDate: undefined,
      endDate: undefined,
    },
    onSubmit: () => {},
  });

  const navigate = useNavigate();
  const navigateToInventory = () => {
    navigate(`/branchs/${id}/maintenance/inventory`);
  };
  //STATE PARA MANEJAR OPEN/CLOSE DE LOS FILTROS
  const [filters, setFilters] = useState(false);
  const filtersOpen = () => {
    setFilters(!filters);
  };
  //OPEN FOR DIALOG CREATE
  const [open, setOpen] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);

  const openDialogEdit = () => {
    setOpenEdit(!openEdit);
  };

  const openDialog = () => {
    if (inventoriesQuery?.data?.length === 0) {
      setOpenCreateInventoryDialog(true);
      return;
    }

    setOpen(!open);
  };

  const debouncedSearch = useDebounce(maintenanceFormik.values.search, 500);
  const inventoriesQuery = useInventoriesQuery({ branchId: id });
  const inventoryItemsQuery = useInventoryItemsQuery({ branchId: id });
  const maintenanceQuery = useMaintenanceQuery({
    branchId: id,
    search: debouncedSearch,
    state: maintenanceFormik.values.state,
    urgency: maintenanceFormik.values.urgency,
    assignedId: maintenanceFormik.values.assignedId,
    startDate: maintenanceFormik.values.startDate,
    endDate: maintenanceFormik.values.endDate,
    page,
    pageSize,
  });

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleClose = () => {
    setCurrentMaintenance(undefined);
  };

  useEffect(() => {
    if (
      maintenanceFormik.values.state ||
      maintenanceFormik.values.urgency ||
      maintenanceFormik.values.assignedId ||
      maintenanceFormik.values.startDate ||
      maintenanceFormik.values.endDate
    ) {
      setPage(1);
    } else {
      setPage(page);
    }
  }, [maintenanceFormik.values]);

  const handleUpdateMaintenance = async (values) => {
    try {
      const items_inventory =
        values.inventoryItemIds?.map((itemId) => ({
          item_id: itemId,
          maintenance_id: currentMaintenance.id,
        })) || [];

      await updateMaintenance({
        ...values,
        branchId: id,
        id: currentMaintenance.id,
        items_inventory,
      }).unwrap();
      toasts.success(
        "Actualización exitosa",
        "El mantenimiento ha sido actualizado correctamente"
      );
    } catch (error) {
      console.error("Error in handleUpdateMaintenance:", error);
      toasts.danger("Error al actualizar", "Por favor, intenta de nuevo");
      throw error;
    }
  };

  const handleCreateMaintenance = async (values) => {
    try {
      await createMaintenance({ ...values, branchId: id }).unwrap();
      toasts.success(
        "Registro exitoso",
        "El mantenimiento ha sido creado correctamente"
      );
    } catch (error) {
      toasts.danger("Error al crear", "Por favor, intenta de nuevo");
      throw error;
    }
  };

  const handleFavoriteMaintenance = async (maintenance) => {
    try {
      await updateMaintenance({
        branchId: id,
        id: maintenance.id,
        favorite: !maintenance.favorite,
      }).unwrap();
      toasts.success(
        "Actualización",
        "El mantenimiento ha sido marcado como favorito correctamente"
      );
    } catch (error) {
      toasts.danger(
        "Error al actualizar",
        "El mantenimiento no se ha marcado como favorito"
      );
      console.log("Error on handleFavoriteMaintenance", error);
      throw error;
    }
  };

  const handleEditMaintenance = async (maintenance) => {
    try {
      const result = await getMaintenanceById({
        branchId: id,
        id: maintenance.id,
      }).unwrap();

      setCurrentMaintenance(result.data);
      openDialogEdit();
    } catch (error) {
      console.error("Error fetching maintenance:", error);
      toasts.danger("Error", "No se pudo cargar el mantenimiento");
    }
  };

  const handleStatusChange = async (maintenance: any) => {
    try {
      await changeMaintenanceStatus({
        branchId: id,
        id: maintenance.id,
      }).unwrap();
      toasts.success(
        "Estado actualizado",
        "El estado del mantenimiento ha sido cambiado correctamente"
      );
    } catch (error) {
      console.error("Error changing maintenance status:", error);
      toasts.danger("Error al cambiar estado", "Por favor, intenta de nuevo");
      throw error;
    }
  };

  const personal = useMemo(
    () =>
      personalQuery?.data?.data?.data ? personalQuery?.data?.data?.data : [],
    [personalQuery?.data]
  );

  const maintenances = useMemo(
    () => maintenanceQuery?.data?.data ?? [],
    [maintenanceQuery?.data]
  );

  const inventoryItems = useMemo(
    () => inventoryItemsQuery?.data?.data ?? [],
    [inventoryItemsQuery?.data]
  );

  return (
    <div className="flex flex-col flex-1 h-full w-full overflow-y-scroll pt-8">
      <div className="flex flex-1 flex-col w-full sm:max-w-full md:max-w-full lg:max-w-full xl:max-w-full 2xl:max-w-full mx-auto mb-8">
        <TableListMaintenance
          onFavorite={(maintenance) => handleFavoriteMaintenance(maintenance)}
          canViewInventory={can(Permission.BRANCH_ACCESS_VIEW_INVENTORY)}
          search={maintenanceFormik.values.search}
          handleChange={maintenanceFormik.handleChange}
          isLoading={maintenanceQuery.isLoading}
          title={t("maintenance.views.title")}
          description={t("maintenance.views.subtittle")}
          maintenances={maintenances}
          functionButtonEdit={handleEditMaintenance}
          functionButtonCreate={openDialog}
          filterButton={filtersOpen}
          functionButtonInventory={navigateToInventory}
          onStatusChange={handleStatusChange}
          canCreateMaintenance={can(
            Permission.BRANCH_ACCESS_CREATE_MAINTENANCE
          )}
          canEditMaintenance={can(Permission.BRANCH_ACCESS_EDIT_MAINTENANCE)}
          canCompleteMaintenance={can(
            Permission.BRANCH_ACCESS_COMPLETE_MAINTENANCE
          )}
          canCreateInventory={can(Permission.BRANCH_ACCESS_CREATE_INVENTORY)}
        />
        {maintenanceQuery.data && (
          <PaginationUI
            currentPage={page}
            totalPages={Math.ceil(
              (maintenanceQuery?.data.total ?? 0) / pageSize
            )}
            onPageChange={handlePageChange}
            pageSize={pageSize}
            totalResults={maintenanceQuery?.data.total ?? 0}
          />
        )}

        <Formik
          enableReinitialize
          initialValues={maintenanceInitialValues}
          validationSchema={maintenanceValidationSchema}
          onSubmit={async (values, formikHelpers) => {
            try {
              if (currentMaintenance) {
                await handleUpdateMaintenance(values);
              } else {
                await handleCreateMaintenance({
                  ...values,
                  branchId: id,
                });
              }
            } catch (error) {
              throw error;
            } finally {
              setCurrentMaintenance(undefined);
              setOpen(false);
              formikHelpers.resetForm();
            }
          }}
        >
          {(formik) => {
            return (
              <DialogCreate
                isLoading={createMaintenanceResult.isLoading}
                acceptDisabled={createMaintenanceResult.isLoading}
                cancelDisabled={createMaintenanceResult.isLoading}
                OnclickCreate={async () => {
                  formik.handleSubmit();
                }}
                OnclickCancel={() => {
                  setOpen(false);
                  formik.resetForm();
                  setCurrentMaintenance(undefined);
                }}
                tittleButtonCreate={t("general.create")}
                tittleButtonCancel={t("general.cancel")}
                open={open}
                setOpen={setOpen}
                tittle={t("maintenance.views.new_task")}
                subTittle={t("maintenance.views.creation_task")}
                onClose={() => {
                  handleClose();
                  formik.resetForm();
                }}
              >
                <FormMaintenance
                  formik={formik}
                  personal={personal}
                  inventoryItems={inventoryItems}
                  isEditingMaintenance={currentMaintenance || false}
                />
              </DialogCreate>
            );
          }}
        </Formik>

        {/* EDIT MAINTENANCE */}
        <Formik
          enableReinitialize
          initialValues={{
            ...currentMaintenance,
            inventoryItemIds: currentMaintenance?.items_inventory?.map(
              (item: any) => item.item_id
            ),
            assignedId: currentMaintenance?.assigned_id,
          }}
          validationSchema={editMaintenanceValidationSchema}
          onSubmit={async (values, formikHelpers) => {
            try {
              console.log("Submit values:", values);
              // Asegurarnos que inventoryItemIds esté presente en los valores
              const updatedValues = {
                ...values,
                inventoryItemIds:
                  values.inventoryItemIds ||
                  values.items_inventory?.map((item: any) => item.item_id) ||
                  [],
              };
              console.log("Updated values to submit:", updatedValues);
              await handleUpdateMaintenance(updatedValues);
            } catch (error) {
              console.error("Error in submit:", error);
              throw error;
            } finally {
              setCurrentMaintenance(undefined);
              setOpenEdit(false);
              formikHelpers.resetForm();
            }
          }}
        >
          {(formik) => {
            return (
              <DialogCreate
                isLoading={updateMaintenanceResult.isLoading}
                acceptDisabled={updateMaintenanceResult.isLoading}
                cancelDisabled={updateMaintenanceResult.isLoading}
                OnclickCreate={async () => {
                  formik.handleSubmit();
                }}
                OnclickCancel={() => {
                  setOpenEdit(false);
                  formik.resetForm();
                  setCurrentMaintenance(undefined);
                }}
                tittleButtonCreate={t("general.edit")}
                tittleButtonCancel={t("general.cancel")}
                open={openEdit}
                setOpen={setOpenEdit}
                tittle={t("maintenance.views.edit_task")}
                subTittle={t("maintenance.views.edition_task")}
                onClose={() => {
                  handleClose();
                  formik.resetForm();
                }}
              >
                <FormMaintenance
                  formik={formik}
                  personal={personal}
                  inventoryItems={inventoryItems}
                  isEditingMaintenance={currentMaintenance || false}
                />
              </DialogCreate>
            );
          }}
        </Formik>
      </div>
      {filters ? (
        <>
          <FiltersMaintenance
            filtersOpen={filtersOpen}
            formik={maintenanceFormik}
            personal={personal}
          />
        </>
      ) : (
        <></>
      )}

      <AlertErrorButtons
        open={openCreateInventoryDialog}
        onClose={() => setOpenCreateInventoryDialog(false)}
        title={"No hay inventario disponible"}
        message={"Por favor cree un inventario"}
        primaryButtonText={t("general.create")}
        onPrimaryButtonClick={() => {
          setOpenCreateInventoryDialog(false);
          navigate(`/branchs/${id}/maintenance/inventory`);
        }}
        onSecondaryButtonClick={() => setOpenCreateInventoryDialog(false)}
        secondaryButtonText={t("general.cancel")}
      />
    </div>
  );
};
